<template>
    <div class="ajustes-detail-container">
        <!-- Estado de carga -->
        <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
            <p class="mt-2 text-muted">{{ internalLabels.loading || 'Cargando...' }}</p>
        </div>

        <!-- Tabla de destino con CorporateDataTable -->
        <div
            v-if="!isLoading && ajustDestino && ajustDestino.length > 0"
            class="ajustes-detail-table-container"
        >
            <CorporateDataTable
                :items="ajustDestino"
                :fields="tableFields"
                :labels="tableLabels"
                :default-per-page="defaultPerPage"
                :page-options="pageOptions"
                :narrow="false"
                :table-min-height="490"
                :table-max-height="490"
                :show-search="true"
                :allow-column-toggle="true"
                :enable-sorting="true"
                :filter-defs="tableFilterDefs"
                :toolbar-disabled="false"
                :show-back-button="showBackButton"
                :back-button-text="internalLabels.volver || 'Volver'"
                @back="goBack"
                class="ajustes-detail-table"
                :default-hidden-columns="defaultHiddenColumns"
            >
                <template #toolbar-actions>
                    <div class="detail-toolbar-left d-flex align-items-center">
                        <!-- ESPACIO VACÍO -->
                    </div>
                    <div class="detail-toolbar-right d-flex align-items-center">
                        <!-- Botón Ajustar Todo solo visible si NO es readOnly, opción 2 y NO bloqueada -->
                        <b-button
                            v-if="!readOnly && infoOpcion === 2 && !secuenciaBloqueada"
                            size="sm"
                            class="table-toolbar-add-btn table-toolbar-add-btn-dest-all"
                            @click="ajustarTodo()"
                        >
                            <font-awesome-icon :icon="['fas', 'playlist-add-check']" class="mr-1" />
                            {{ internalLabels.ajustarTodo || 'Ajustar Todo' }}
                        </b-button>
                    </div>
                </template>

                <!-- Estado vacío -->
                <template #empty-state>
                    <div class="text-center py-5">
                        <font-awesome-icon :icon="['fas', 'file-alt']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
                        <p class="h5 text-muted">{{ internalLabels.noData || 'No hay cuentas de destino registradas' }}</p>
                    </div>
                </template>

                <!-- Columnas -->
                <template #cell(cuenta)="data">
                    <div class="text-left font-weight-bold">{{ data.value }}</div>
                </template>

                <template #cell(descripcion)="data">
                    <div class="text-left">{{ data.value }}</div>
                </template>

                <template #cell(saldo_actual)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(funcional1)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(funcional2)="data">
                    <div class="text-right">{{ formatCurrency(data.value) || '-' }}</div>
                </template>

                <template #cell(funcional3)="data">
                    <div class="text-right">{{ formatCurrency(data.value) || '-' }}</div>
                </template>

                <template #cell(ajuste_actual)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(ajuste_funcional1)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <!-- Columna Editar - SOLO visible si NO es readOnly, es Modificar y NO bloqueada -->
                <template #cell(accionEditar)="data">
                    <b-button
                        v-if="!readOnly && infoOpcion === 2 && !secuenciaBloqueada"
                        style="background-color: #FF924C; width: 30px; height: 30px; border: none; border-radius: 4px;"
                        class="md-fab md-primary table-action-btn"
                        @click="editAjuste(data.item)"
                    >
                        <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" />
                    </b-button>
                </template>
            </CorporateDataTable>
        </div>

        <!-- Mensaje sin datos -->
        <div v-if="!isLoading && (!ajustDestino || ajustDestino.length === 0)" class="admin-table-block">
            <h5>{{ internalLabels.noData || 'No hay cuentas de destino registradas' }}</h5>
        </div>

        <!-- Modal de edición -->
        <b-modal v-model="showDialogEdit" :title="tituloModal" size="lg" @hidden="showDialogEdit = false">
            <b-form @submit.prevent="guardarAjuste">
                <b-row>
                    <b-col cols="2"><h6><b>{{ labels.cuenta }}</b></h6></b-col>
                    <b-col cols="2"><h6>{{ formEdit?.cuenta }}</h6></b-col>
                    <b-col cols="8"><h6>{{ formEdit?.descripcion }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="2"><h6><b>{{ labels.saldo_actual }}</b></h6></b-col>
                    <b-col cols="2"></b-col>
                    <b-col cols="8"><h6>{{ formatCurrency(formEdit?.saldo_actual) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.funcional1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.promedio1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.interna1 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.funcional1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.promedio1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.interna1) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.funcional2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.promedio2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.interna2 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.funcional2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.promedio2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.interna2) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.funcional3 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.promedio3 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.interna3 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.funcional3) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.promedio3) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.interna3) }}</h6></b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col cols="3"><h6><b>{{ labels.ajuste_actual }}</b></h6></b-col>
                    <b-col cols="9">
                        <b-form-input
                            v-model="v_ajuste_actual"
                            type="number"
                            step="0.01"
                            @input="calcularAjustes"
                            :disabled="readOnly || eliminar || secuenciaBloqueada"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_funcional1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_promedio1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_interna1 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_funcional1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_promedio1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_interna1) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_funcional2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_promedio2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_interna2 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_funcional2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_promedio2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_interna2) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_funcional3 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_promedio3 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_interna3 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_funcional3) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_promedio3) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_interna3) }}</h6></b-col>
                </b-row>
            </b-form>

            <template #footer>
                <b-button v-if="!eliminar" variant="secondary" @click="restaurarAjuste">
                    {{ labels.restaurar || 'Restaurar' }}
                </b-button>
                <b-button v-if="!eliminar" variant="primary" @click="guardarAjuste" :disabled="readOnly || secuenciaBloqueada">
                    {{ labels.guardar || 'Guardar' }}
                </b-button>
                <b-button v-if="eliminar" variant="danger" @click="eliminarAjuste" :disabled="readOnly || secuenciaBloqueada">
                    {{ labels.eliminar || 'Eliminar' }}
                </b-button>
                <b-button variant="secondary" @click="showDialogEdit = false">
                    {{ labels.cerrar || 'Cerrar' }}
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import Balance from "@/services/BalanceNiif"
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "EliminaDestino",
    components: { CorporateDataTable },
    props: {
        ajustDestino: { type: Array, default: () => [] },
        codReporte: { type: [String, Number], default: null },
        id_compania: { type: [String, Number], default: null },
        vigencia: { type: [String, Number], default: null },
        nombreCompania: { type: String, default: '' },
        readOnly: { type: Boolean, default: false },
        // NUEVA PROP: bloqueo por secuencia >= 500
        secuenciaBloqueada: { type: Boolean, default: false },
        infoOpcion: { type: Number, default: 0 },
        infoSecuencia: { type: [String, Number], default: null },
        infoTipoAjuste: { type: Number, default: 0 },
        infoVigencia: { type: [String, Number], default: null },
        tableLabels: { type: Object, default: () => ({}) },
        tableFilterDefs: { type: Array, default: () => [] },
        defaultPerPage: { type: Number, default: 10 },
        pageOptions: { type: Array, default: () => [10, 20, 50, 100] },
        showBackButton: { type: Boolean, default: false }
    },
    data: () => ({
        showDialogEdit: false,
        formEdit: {},
        v_ajuste_actual: 0,
        v_ajuste_funcional1: 0,
        v_ajuste_promedio1: 0,
        v_ajuste_interna1: 0,
        v_ajuste_funcional2: 0,
        v_ajuste_promedio2: 0,
        v_ajuste_interna2: 0,
        v_ajuste_funcional3: 0,
        v_ajuste_promedio3: 0,
        v_ajuste_interna3: 0,
        eliminar: false,
        isLoading: false,
        porcentaje: 1
    }),
    computed: {
        internalLabels() {
            const es = this.$store.state._language == "es-ES"
            return {
                ...this.tableLabels,
                cuenta: es ? "Cuenta" : "Account",
                descripcion: es ? "Descripción" : "Description",
                saldo_actual: es ? "Saldo" : "Balance",
                funcional1: es ? "Funcional" : "Functional",
                funcional2: es ? "Funcional 2" : "Functional 2",
                funcional3: es ? "Funcional 3" : "Functional 3",
                promedio1: es ? "Promedio" : "Average",
                promedio2: es ? "Promedio 2" : "Average 2",
                promedio3: es ? "Promedio 3" : "Average 3",
                interna1: es ? "Interna" : "Internal",
                interna2: es ? "Interna 2" : "Internal 2",
                interna3: es ? "Interna 3" : "Internal 3",
                ajuste_actual: es ? "Ajuste Saldo" : "Balance Adjustment",
                ajuste_funcional1: es ? "Ajuste Funcional" : "Functional Adjustment",
                ajuste_promedio1: es ? "Ajuste Promedio" : "Average Adjustment",
                ajuste_interna1: es ? "Ajuste Interna" : "Internal Adjustment",
                ajuste_funcional2: es ? "Ajuste Funcional 2" : "Functional Adjustment 2",
                ajuste_promedio2: es ? "Ajuste Promedio 2" : "Average Adjustment 2",
                ajuste_interna2: es ? "Ajuste Interna 2" : "Internal Adjustment 2",
                ajuste_funcional3: es ? "Ajuste Funcional 3" : "Functional Adjustment 3",
                ajuste_promedio3: es ? "Ajuste Promedio 3" : "Average Adjustment 3",
                ajuste_interna3: es ? "Ajuste Interna 3" : "Internal Adjustment 3",
                editar: es ? "Editar" : "Edit",
                eliminar: es ? "Eliminar" : "Delete",
                restaurar: es ? "Restaurar" : "Restore",
                guardar: es ? "Guardar" : "Save",
                cerrar: es ? "Cerrar" : "Close",
                ajustarTodo: es ? "Ajustar Todo" : "Adjust All",
                volver: es ? "Volver" : "Back",
                noData: es ? "No hay cuentas de destino registradas" : "No destination accounts registered",
                loading: es ? "Cargando..." : "Loading...",
            }
        },
        defaultHiddenColumns() {
            return ['saldo_actual', 'funcional1', 'funcional2', 'funcional3']
        },
        labels() { return this.internalLabels },
        tableFields() {
            const fields = [
                { key: 'cuenta', label: this.internalLabels.cuenta, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'descripcion', label: this.internalLabels.descripcion, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'saldo_actual', label: this.internalLabels.saldo_actual, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'funcional1', label: this.internalLabels.funcional1, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'funcional2', label: this.internalLabels.funcional2, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'funcional3', label: this.internalLabels.funcional3, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'ajuste_actual', label: this.internalLabels.ajuste_actual, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'ajuste_funcional1', label: this.internalLabels.ajuste_funcional1, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' }
            ]

            // La columna de acciones solo existe si el botón de editar se puede mostrar
            if (!this.readOnly && this.infoOpcion === 2 && !this.secuenciaBloqueada) {
                fields.push({ key: 'accionEditar', label: '', thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '60px' })
            }

            return fields
        },
        tituloModal() {
            const tipo = this.tipoAjusteTexto(this.formEdit?.tipo_ajuste)
            return `${this.nombreCompania || 'Destino'} ${tipo}`
        }
    },
    methods: {
        goBack() { this.$emit('back') },

        tipoAjusteTexto(tipo) {
            const es = this.$store.state._language == "es-ES"
            if (tipo == 1) return es ? 'Interes no controlante' : 'Non-controlling interest'
            if (tipo == 2) return es ? 'Eliminación en inversión' : 'Investment elimination'
            if (tipo == 3) return es ? 'Eliminación saldo recíproco' : 'Reciprocal balance elimination'
            if (tipo == 4) return es ? 'Ajuste del Combinado' : 'Combined adjustment'
            return es ? 'Tipo desconocido' : 'Unknown type'
        },

        formatCurrency(value) {
            if (value === 0 || value === "0" || value === null || value === "" || value === undefined) return ""
            return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
        },

        async verificarYActualizarValoresCero(item, tipo) {
            const camposAverificar = [
                'saldo_actual', 'funcional1', 'funcional2', 'funcional3',
                'promedio1', 'promedio2', 'promedio3', 'interna1', 'interna2', 'interna3',
                'ajuste_actual', 'ajuste_funcional1', 'ajuste_funcional2', 'ajuste_funcional3',
                'ajuste_promedio1', 'ajuste_promedio2', 'ajuste_promedio3',
                'ajuste_interna1', 'ajuste_interna2', 'ajuste_interna3'
            ];

            let hayCeros = false;
            for (const campo of camposAverificar) {
                if (item[campo] === 0 || item[campo] === "0") {
                    hayCeros = true;
                    break;
                }
            }

            if (!hayCeros) return false;

            const valoresActualizados = { ...item };
            for (const campo of camposAverificar) {
                if (valoresActualizados[campo] === 0 || valoresActualizados[campo] === "0") {
                    valoresActualizados[campo] = 1;
                }
            }

            try {
                await Balance.actualizarValoresCero(tipo, valoresActualizados);
                this.$toast.info("Se han actualizado valores de 0 a 1 para evitar divisiones por cero");
                return true;
            } catch (error) {
                this.$toast.error("Error al actualizar valores");
                console.error("Error:", error);
                return false;
            }
        },

        async obtenerItemActualizado(itemOriginal) {
            try {
                const respuesta = await Balance.obtenerItemActualizado(
                    this.infoVigencia || this.vigencia,
                    this.infoSecuencia,
                    itemOriginal.cuenta,
                    "D"
                );
                if (respuesta.data && respuesta.data.item) {
                    return respuesta.data.item;
                }
                return null;
            } catch (error) {
                console.error("Error al obtener item actualizado:", error);
                return null;
            }
        },

        async editAjuste(item) {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para edición.' : 'The validity is disabled for editing.')
                return
            }

            // NUEVO: bloqueo por secuencia >= 500
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para edición.' : 'This sequence (500+) is locked for editing.')
                return
            }

            this.eliminar = item.origen === "C"

            try {
                const actualizado = await this.verificarYActualizarValoresCero(item, "D");
                if (actualizado) {
                    const itemActualizado = await this.obtenerItemActualizado(item);
                    if (itemActualizado) {
                        this.formEdit = { ...itemActualizado };
                    } else {
                        this.formEdit = { ...item };
                    }
                } else {
                    this.formEdit = { ...item };
                }
            } catch (error) {
                console.error("Error al actualizar valores:", error);
                this.formEdit = { ...item };
            }

            this.v_ajuste_actual = this.formEdit.ajuste_actual || 0
            this.v_ajuste_funcional1 = this.formEdit.ajuste_funcional1 || 0
            this.v_ajuste_promedio1 = this.formEdit.ajuste_promedio1 || 0
            this.v_ajuste_interna1 = this.formEdit.ajuste_interna1 || 0
            this.v_ajuste_funcional2 = this.formEdit.ajuste_funcional2 || 0
            this.v_ajuste_promedio2 = this.formEdit.ajuste_promedio2 || 0
            this.v_ajuste_interna2 = this.formEdit.ajuste_interna2 || 0
            this.v_ajuste_funcional3 = this.formEdit.ajuste_funcional3 || 0
            this.v_ajuste_promedio3 = this.formEdit.ajuste_promedio3 || 0
            this.v_ajuste_interna3 = this.formEdit.ajuste_interna3 || 0
            this.showDialogEdit = true
        },

        calcularAjustes() {
            const saldo = parseFloat(this.formEdit.saldo_actual) || 0
            const ajuste = parseFloat(this.v_ajuste_actual) || 0

            if (this.v_ajuste_actual === "" || this.v_ajuste_actual === null || this.v_ajuste_actual === undefined) {
                this.v_ajuste_funcional1 = 0
                this.v_ajuste_promedio1 = 0
                this.v_ajuste_interna1 = 0
                this.v_ajuste_funcional2 = 0
                this.v_ajuste_promedio2 = 0
                this.v_ajuste_interna2 = 0
                this.v_ajuste_funcional3 = 0
                this.v_ajuste_promedio3 = 0
                this.v_ajuste_interna3 = 0
                return
            }

            if (saldo === 0) {
                this.v_ajuste_funcional1 = ajuste
                this.v_ajuste_promedio1 = ajuste
                this.v_ajuste_interna1 = ajuste
                this.v_ajuste_funcional2 = ajuste
                this.v_ajuste_promedio2 = ajuste
                this.v_ajuste_interna2 = ajuste
                this.v_ajuste_funcional3 = ajuste
                this.v_ajuste_promedio3 = ajuste
                this.v_ajuste_interna3 = ajuste
                return
            }
            const porcentaje = ajuste / saldo
            this.v_ajuste_funcional1 = Math.round((this.formEdit.funcional1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_promedio1 = Math.round((this.formEdit.promedio1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_interna1 = Math.round((this.formEdit.interna1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_funcional2 = Math.round((this.formEdit.funcional2 || 0) * porcentaje * 100) / 100
            this.v_ajuste_promedio2 = Math.round((this.formEdit.promedio2 || 0) * porcentaje * 100) / 100
            this.v_ajuste_interna2 = Math.round((this.formEdit.interna2 || 0) * porcentaje * 100) / 100
            this.v_ajuste_funcional3 = Math.round((this.formEdit.funcional3 || 0) * porcentaje * 100) / 100
            this.v_ajuste_promedio3 = Math.round((this.formEdit.promedio3 || 0) * porcentaje * 100) / 100
            this.v_ajuste_interna3 = Math.round((this.formEdit.interna3 || 0) * porcentaje * 100) / 100
        },

        restaurarAjuste() {
            this.v_ajuste_actual = -(this.formEdit.saldo_actual || 0)
            this.calcularAjustes()
        },

        async guardarAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para edición.' : 'The validity is disabled for editing.')
                return
            }

            // NUEVO: bloqueo por secuencia >= 500
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para guardar.' : 'This sequence (500+) is locked for saving.')
                return
            }

            if (this.v_ajuste_actual === "" || this.v_ajuste_actual === null || this.v_ajuste_actual === undefined) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Diligenciar el campo Ajuste Actual" : "Fill in the Actual Adjustment field")
                return
            }

            this.v_ajuste_actual = parseFloat(this.v_ajuste_actual) || 0

            this.showDialogEdit = false
            const data = {
                ...this.formEdit,
                ajuste_actual: this.v_ajuste_actual,
                ajuste_funcional1: this.v_ajuste_funcional1,
                ajuste_promedio1: this.v_ajuste_promedio1,
                ajuste_interna1: this.v_ajuste_interna1,
                ajuste_funcional2: this.v_ajuste_funcional2,
                ajuste_promedio2: this.v_ajuste_promedio2,
                ajuste_interna2: this.v_ajuste_interna2,
                ajuste_funcional3: this.v_ajuste_funcional3,
                ajuste_promedio3: this.v_ajuste_promedio3,
                ajuste_interna3: this.v_ajuste_interna3,
            }
            try {
                await Balance.grabeAjuste("D", data)
                this.$toast.success(this.$store.state._language == "es-ES" ? "Información Guardada" : "Information Saved")
                this.$emit('ajusteGuardado')
            } catch (error) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al guardar" : "Error saving")
            }
        },

        async eliminarAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para eliminar.' : 'The validity is disabled for deleting.')
                return
            }

            // NUEVO: bloqueo por secuencia >= 500
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para eliminar.' : 'This sequence (500+) is locked for deleting.')
                return
            }

            this.showDialogEdit = false
            try {
                await Balance.elimineAjuste("D", this.formEdit)
                this.$toast.success(this.$store.state._language == "es-ES" ? "Información Eliminada" : "Information Deleted")
                this.$emit('ajusteGuardado')
                this.$emit('cierraDetalle')
            } catch (error) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al eliminar" : "Error deleting")
            }
        },

        async ajustarTodo() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para ajustar.' : 'The validity is disabled for adjusting.')
                return
            }

            // NUEVO: bloqueo por secuencia >= 500
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para ajustar.' : 'This sequence (500+) is locked for adjusting.')
                return
            }

            if (!this.ajustDestino || this.ajustDestino.length === 0) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "No hay registros para ajustar" : "No records to adjust")
                return
            }

            const confirmar = confirm(this.$store.state._language == "es-ES"
                ? `¿Ajustar todos los registros?\nEsta acción aplicará el ajuste automático a ${this.ajustDestino.length} registros`
                : `Adjust all records?\nThis action will apply automatic adjustment to ${this.ajustDestino.length} records`
            )
            if (!confirmar) return

            await this.ejecutarAjusteTotal()
        },

        async ejecutarAjusteTotal() {
            let registrosAjustados = 0
            let errores = 0
            let registrosOmitidos = 0

            for (const item of this.ajustDestino) {
                if (item.origen === "C") continue

                const ajusteActualExistente = parseFloat(item.ajuste_actual)
                const tieneAjusteManual = !isNaN(ajusteActualExistente) &&
                    (ajusteActualExistente > 1 || ajusteActualExistente < -1)

                if (tieneAjusteManual) {
                    registrosOmitidos++
                    continue
                }

                try {
                    const ajusteActual = -(item.saldo_actual || 0)
                    await Balance.grabeAjuste("D", {
                        ...item,
                        ajuste_actual: ajusteActual,
                        ajuste_funcional1: -(item.funcional1 || 0),
                        ajuste_promedio1: -(item.promedio1 || 0),
                        ajuste_interna1: -(item.interna1 || 0),
                        ajuste_funcional2: -(item.funcional2 || 0),
                        ajuste_promedio2: -(item.promedio2 || 0),
                        ajuste_interna2: -(item.interna2 || 0),
                        ajuste_funcional3: -(item.funcional3 || 0),
                        ajuste_promedio3: -(item.promedio3 || 0),
                        ajuste_interna3: -(item.interna3 || 0)
                    })
                    registrosAjustados++
                } catch (error) {
                    errores++
                    console.error("Error ajustando registro:", error)
                }
            }

            if (errores === 0) {
                if (registrosOmitidos > 0) {
                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Se ajustaron ${registrosAjustados} registros y se omitieron ${registrosOmitidos} con ajuste existente`
                        : `Adjusted ${registrosAjustados} records and skipped ${registrosOmitidos} with existing adjustment`
                    )
                } else {
                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Se ajustaron ${registrosAjustados} registros correctamente`
                        : `Adjusted ${registrosAjustados} records successfully`
                    )
                }
            } else {
                this.$toast.warning(this.$store.state._language == "es-ES"
                    ? `Se ajustaron ${registrosAjustados} registros, se omitieron ${registrosOmitidos} y hubo ${errores} errores`
                    : `Adjusted ${registrosAjustados} records, skipped ${registrosOmitidos} and ${errores} errors`
                )
            }

            this.$emit('ajusteGuardado')
        }
    }
}
</script>

<style src="../main.css"></style>
<style scoped>
.ajustes-detail-container { padding: 0; width: 100%; }
.ajustes-detail-table-container { width: 100%; }
.ajustes-detail-table { width: 100%; overflow-x: auto; }
.detail-toolbar-left { flex: 1; min-width: 0; }
.detail-toolbar-right { flex: 0 0 auto; display: flex; align-items: center; gap: 4px; }
.table-action-btn { border: none; border-radius: 4px; transition: all 0.2s; }
.table-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.table-toolbar-add-btn { height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px; }
.table-toolbar-add-btn-dest-all { background-color: #ffc107; color: #212529; border: 1px solid #ffc107; }
.table-toolbar-add-btn-dest-all:hover:not(:disabled) { background-color: #e0a800; border-color: #d39e00; }
.table-toolbar-add-btn-dest-all:disabled { opacity: 0.65; cursor: not-allowed; }
.admin-table-block { background: white; border-radius: 8px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.admin-table-block h5 { color: #6c757d; text-align: center; margin: 0; }
@media (max-width: 768px) {
    .ajustes-detail-container { min-height: 450px; }
    .detail-toolbar-left h5 { font-size: 0.8rem; }
    .table-toolbar-add-btn { font-size: 11px; padding: 0 10px; height: 28px; min-height: 28px; }
}
@media (max-width: 576px) {
    .ajustes-detail-container { min-height: 400px; }
    .detail-toolbar-right { flex-wrap: wrap; gap: 4px; }
    .table-toolbar-add-btn { font-size: 10px; padding: 0 8px; height: 26px; min-height: 26px; }
}
</style>